import { useEffect, useState } from "react";

import type {
  AuthClient,
  IdentityRow,
  ProvidersResponse,
} from "@nav-games/auth-client";

interface Props {
  auth: AuthClient;
}

type Provider = "apple" | "google" | "magic_link";

const PROVIDER_LABEL: Record<Provider, string> = {
  apple: "Apple",
  google: "Google",
  magic_link: "Email",
};

export function LinkedAccounts({ auth }: Props) {
  const [identities, setIdentities] = useState<IdentityRow[] | null>(null);
  const [providers, setProviders] = useState<ProvidersResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Provider | null>(null);

  const refresh = async () => {
    setError(null);
    try {
      const [ids, prov] = await Promise.all([
        auth.identities(),
        auth.providers(),
      ]);
      setIdentities(ids);
      setProviders(prov);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth]);

  const link = async (provider: Provider) => {
    setError(null);
    setBusy(provider);
    try {
      if (provider === "magic_link") {
        // For email, kick the user into the same SignIn form but
        // carrying a linkIntent — handled by the prompt below.
        const promptEmail = window.prompt(
          "Enter the email address to link. We'll send you a one-time link.",
        );
        if (!promptEmail || promptEmail.trim().length === 0) {
          setBusy(null);
          return;
        }
        const { linkIntent } = (await auth.beginLink("magic_link")) as {
          linkIntent: string;
        };
        // Hand the linkIntent into the existing requestMagicLink call.
        // requestMagicLink itself doesn't yet pipe linkIntent through —
        // do it inline as a raw fetch instead so this UI doesn't need
        // an SDK change.
        const session = auth.session();
        if (!session) throw new Error("no_session");
        const resp = await fetch(
          new URL(
            "/auth/magic/request",
            (auth as unknown as { options: { authBaseUrl: string } }).options
              .authBaseUrl,
          ).toString(),
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({
              app: "nav",
              platform: "web",
              email: promptEmail.trim(),
              returnUrl: window.location.origin + "/auth/callback",
              linkIntent,
            }),
          },
        );
        if (!resp.ok) {
          throw new Error(`request_failed_${resp.status}`);
        }
        window.alert(
          `We've sent a link to ${promptEmail}. Click it from this browser to attach the email to your account.`,
        );
        return;
      }
      const result = (await auth.beginLink(provider)) as { redirectUrl: string };
      window.location.assign(result.redirectUrl);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const unlink = async (row: IdentityRow) => {
    if (!window.confirm(`Unlink ${PROVIDER_LABEL[row.provider]}?`)) return;
    setError(null);
    try {
      await auth.unlinkIdentity(row.provider, row.subject);
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  if (identities === null || providers === null) {
    return (
      <div className="card">
        <h2>Linked accounts</h2>
        <p className="muted small">Loading…</p>
      </div>
    );
  }

  const linkedProviders = new Set(identities.map((i) => i.provider));
  const canAdd: Record<Provider, boolean> = {
    apple: providers.apple && !linkedProviders.has("apple"),
    google: providers.google && !linkedProviders.has("google"),
    magic_link: providers.magicLink && !linkedProviders.has("magic_link"),
  };
  const canUnlink = identities.length > 1;

  return (
    <div className="card">
      <h2>Linked accounts</h2>
      <p className="muted small">
        Sign-in methods attached to this user. You need at least one — the
        unlink button is disabled if it would leave you with none.
      </p>

      {identities.length === 0 ? (
        <p className="small" style={{ color: "var(--warn)" }}>
          No identities found — this shouldn't happen for a signed-in user.
        </p>
      ) : (
        <ul style={{ margin: "12px 0", padding: 0, listStyle: "none" }}>
          {identities.map((id) => (
            <li
              key={`${id.provider}:${id.subject}`}
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                padding: "8px 0",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <span style={{ minWidth: 72 }}>{PROVIDER_LABEL[id.provider]}</span>
              <span
                className="small muted"
                style={{
                  flex: 1,
                  fontFamily: "ui-monospace, monospace",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={id.subject}
              >
                {id.subject.length > 24 ? `${id.subject.slice(0, 12)}…${id.subject.slice(-8)}` : id.subject}
              </span>
              <button
                className="secondary"
                disabled={!canUnlink}
                onClick={() => unlink(id)}
                title={canUnlink ? "Unlink this identity" : "You need to keep at least one sign-in method"}
              >
                Unlink
              </button>
            </li>
          ))}
        </ul>
      )}

      <div
        className="row"
        style={{ flexDirection: "column", alignItems: "stretch", gap: 8 }}
      >
        {(["apple", "google", "magic_link"] as const).map((p) =>
          canAdd[p] ? (
            <button
              key={p}
              type="button"
              className="secondary"
              disabled={busy !== null}
              onClick={() => link(p)}
            >
              {busy === p ? "Linking…" : `Add ${PROVIDER_LABEL[p]}`}
            </button>
          ) : null,
        )}
      </div>

      {error && (
        <p className="small" style={{ color: "var(--danger)", marginTop: 10 }}>
          {error}
        </p>
      )}
    </div>
  );
}
